import dayjs from "dayjs";
import { noop } from "lodash";
import pick from "lodash/pick";

import Sentry from "@similarweb/sentry-logger";

import {
    CHANGE_DAY_COLUMN_PREFIX,
    EColumnsFields,
    FIRST_DAY_COLUMN_PREFIX,
    LAST_DAY_COLUMN_PREFIX,
} from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { getLanguagesForCampaigns } from "app/components/campaigns/shared/locationsAndLanguagesUtils";
import { ERankTrackerPerformanceOvertimeTabs } from "app/components/campaigns/shared/overview/types";
import { sanitizeUseCaseFilter } from "app/components/campaigns/shared/utils";
import { TrafficSource } from "app/components/dashboard/widget-wizard/utils/dashboardWizardTrafficSourceGenerator";
import { RankTrackerKeywordsTableTemplate } from "app/components/widget/dashboard/RankTrackerKeywordsTableTemplate";
import { TableWidget } from "app/components/widget/widget-types/TableWidget";
import {
    KWReportTableWidgetAPIParams,
    KWReportWidgetTableHeadersMap,
} from "app/components/widget/widget-utilities/Campaigns/campaigns.consts";
import {
    getAllCampaigns,
    getDurationForURLWithCampaignMax,
    getDurationToPresent,
    getFiltersTooltip,
    getRTDurationObjectForCampaign,
    getSelectedLocationsForURL,
    getUpdateLocationsOnViewData,
    transformAndCapitalizeTexts,
} from "app/components/widget/widget-utilities/Campaigns/CampaignsUtils";
import { CHART_COLORS } from "app/constants/ChartColors";
import RankTrackerKeywordsReportTableDataFetcherFactory from "app/pages/digital-marketing/keywords-campaigns/dashboard-widgets/RankTrackerKeywordsReportTableDataFetcher";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import {
    getTableMetrics,
    transformDataToRow,
} from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsCamapaignKeywordsReportTableUtils";
import { getDashboardKeywordsReportTableColumns } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportDashboardTableColumnsConfig";
import type {
    IKeywordsReportTableData,
    IKeywordsReportTableMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import { EKeywordsReportKeywordsTableMetrics } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import type { DatesRange } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

export class RankTrackerKeywordsReportTableWidget extends TableWidget {
    private parsedLanguages: ISupportedLanguagesAndLocale[];

    constructor() {
        super();
        (async () => {
            await this.initCampaigns();
        })();
    }

    private initCampaigns = async () => {
        this.parsedCampaigns = await getAllCampaigns();
    };

    static getWidgetMetadataType() {
        return "RankTrackerKeywordsReportTable";
    }

    get templateComponent() {
        return RankTrackerKeywordsTableTemplate;
    }

    static getWidgetResourceType() {
        return "Table";
    }

    static getWidgetDashboardType() {
        return "Table";
    }

    public isPptSupported = () => {
        return false;
    };

    public getFetcherFactory() {
        return RankTrackerKeywordsReportTableDataFetcherFactory;
    }

    private getSelectedScrapingConfigurationsDetailsFromCurrentCampaign(currentCampaign: IRTCurrentCampaignData) {
        // oxlint-disable-next-line typescript/no-explicit-any
        const scrapingConfsFroWidget: any[] = this._widgetConfig.properties.country?.split(",");
        const scrapingConfigurations = currentCampaign?.scrapingConfigurations?.filter((sc) =>
            scrapingConfsFroWidget.some((scFromWidegt) => scFromWidegt === sc.id),
        ); //get just the selected SC from current campaign
        return scrapingConfigurations;
    }

    getProperties() {
        const props = super.getProperties();
        const currentCampaignConfiguration = this.parsedCampaigns?.find(
            (campaign) => campaign.id === this._params.campaignguid,
        );
        props.currentCampaignConfiguration = currentCampaignConfiguration;
        props.campaignguid = this._params.campaignguid;
        return props;
    }

    private updateLocationsOnViewData() {
        const webSource = this._widgetConfig.properties.webSource;
        if (webSource) {
            this.viewData.country = getUpdateLocationsOnViewData(this.parsedLanguages, this._widgetConfig.properties);
        }
    }

    getViewData() {
        super.getViewData();
        if (!this.parsedLanguages) {
            getLanguagesForCampaigns(false).then((languages) => {
                this.parsedLanguages = languages;
                this.updateLocationsOnViewData();
            });
        }
        const webSource = this._widgetConfig.properties.webSource;
        if (!this._widgetConfig.properties.country && !webSource) {
            //in case devices was not selected yet
            return this.viewData;
        }
        const duration = this._widgetConfig.properties.duration;

        const currentCampaign = this._widgetConfig.properties.currentCampaignConfiguration;
        const durationObject: DatesRange = getRTDurationObjectForCampaign(duration, currentCampaign);
        this.viewData.duration = getDurationToPresent(duration, durationObject);
        const subtitleTextItem = getFiltersTooltip(this._params);
        if (subtitleTextItem) {
            this.viewData.subtitleTextItem = transformAndCapitalizeTexts(subtitleTextItem);
        }
        return this.viewData;
    }

    private transformData(rawResponse: IKeywordsReportTableData) {
        const currentCampaign = this._widgetConfig.properties.currentCampaignConfiguration;
        const scrapingConfigurations =
            this.getSelectedScrapingConfigurationsDetailsFromCurrentCampaign(currentCampaign);
        const selectedSites = this._params.keys?.split(",").map((site) => {
            return { id: site };
        });
        const transformedData = transformDataToRow({
            data: rawResponse,
            tableTab: this._params.tableTab,
            tableGranularity: this._params.granularity,
            selectedSites,
            scrapingConfigurations,
            languagesList: this.parsedLanguages,
            duration: this._params.duration,
            campaign: currentCampaign,
            device: this._params.webSource,
            pageNumber: null, //pageNumber not needed
            isDemoCampaign: currentCampaign.isDemo,
            keywordToTargetUrls: null, //targetUrl
        });
        return { Data: transformedData };
    }

    public async onNewDataReceived(response: IKeywordsReportTableData): Promise<void> {
        const transformedResponse = this.transformData(response);
        await super.onNewDataReceived(transformedResponse);

        // There's some race condition going on with the router, where it first
        // can't generate the proper URL despite passing it all correct params,
        // so we re-set the proUrl here a bit later. This ensures the widget
        // title link updates to the correct URL.
        this.mergeViewData({
            proUrl: this.getProUrl(),
        });
    }

    public getProUrl() {
        const currentCampaignConfiguration = this._widgetConfig.properties.currentCampaignConfiguration;
        if (!currentCampaignConfiguration) {
            return super.getProUrl();
        }

        const queryParams = {
            ...this._params,
            duration: getDurationForURLWithCampaignMax(this._params.duration, currentCampaignConfiguration),
            campaignId: this._params.campaignguid,
            graphTab: ERankTrackerPerformanceOvertimeTabs.VISIBILITY,
            locationsIds: getSelectedLocationsForURL(
                this._widgetConfig.properties.country,
                currentCampaignConfiguration?.scrapingConfigurations,
            ),
            device:
                this._params.webSource === DEVICES_TYPES_FOR_API.MOBILE ? TrafficSource.MOBILE : this._params.webSource,
            sites: this._params.selectedSites,
            tableTab:
                this._params.selectedSites.indexOf(",") > 0
                    ? EKeywordsReportKeywordsTableMetrics.POSITION
                    : EKeywordsReportKeywordsTableMetrics.ALL,
        };
        const apiParams = pick(queryParams, KWReportTableWidgetAPIParams);
        return this._swNavigator.href(this._metricConfig.state, {
            ...apiParams,
            useCaseFilter: sanitizeUseCaseFilter(
                apiParams.useCaseFilter,
                currentCampaignConfiguration.positionExcludedSerpFeatures,
            ),
        });
    }

    private getSingleModeColumns(tableMetricDefinitions: IKeywordsReportTableMetrics[]) {
        const site = this._params.keys;
        const metrics = tableMetricDefinitions.reduce<string[]>((acc, metric: IKeywordsReportTableMetrics) => {
            acc.push(
                `${LAST_DAY_COLUMN_PREFIX}${metric.metricID}${site}`,
                `${FIRST_DAY_COLUMN_PREFIX}${metric.metricID}${site}`,
                `${CHANGE_DAY_COLUMN_PREFIX}${metric.metricID}${site}`,
            );
            return acc;
        }, []);
        return [
            ...metrics,
            EColumnsFields.KEYWORD,
            EColumnsFields.VOLUME,
            EColumnsFields.POSITION_TREND,
            EColumnsFields.TARGET_URL,
            EColumnsFields.GEO_LOCATION,
        ];
    }

    private getCompareModeColumns(sites: string[], tableMetricDefinitions: IKeywordsReportTableMetrics) {
        return [
            ...sites.map((site) => `${LAST_DAY_COLUMN_PREFIX}${tableMetricDefinitions.metricID}${site}`),
            EColumnsFields.KEYWORD,
            EColumnsFields.VOLUME,
            EColumnsFields.GEO_LOCATION,
        ];
    }

    private getTableColumnsHeader(column): string {
        const isFirstDayColumn = column.field.includes(FIRST_DAY_COLUMN_PREFIX);
        const isLastDayColumn = column.field.includes(LAST_DAY_COLUMN_PREFIX);
        const isChangeColumn = column.field.includes(CHANGE_DAY_COLUMN_PREFIX);
        if (isFirstDayColumn || isLastDayColumn || isChangeColumn) {
            return column.displayName;
        }
        return KWReportWidgetTableHeadersMap[column.field];
    }

    protected getMetricColumnsConfig() {
        try {
            const currentCampaign: IRTCurrentCampaignData = this._widgetConfig.properties.currentCampaignConfiguration;
            const durationObject: DatesRange = getRTDurationObjectForCampaign(this._params.duration, currentCampaign);
            const allSites = this._params.keys?.split(",");
            const isSingleMode = allSites.length === 1;
            const sortedColumn = this._params.orderBy?.split(":")?.[0];
            const coloredSelectedSites = allSites.map((site, index) => {
                return { id: site, color: CHART_COLORS.compareMainColors[index] };
            });

            const rawColumns = getDashboardKeywordsReportTableColumns(
                durationObject,
                sortedColumn,
                this._params.asc ? "asc" : "desc",
                coloredSelectedSites,
                this._params.tableTab,
                this._params.tableGranularity ?? GranularityNames.DAILY,
                noop,
                noop,
                false,
                this._params.locationsIds,
                currentCampaign?.lastMigrationDate ? dayjs(currentCampaign?.lastMigrationDate) : null,
                currentCampaign?.lastScrapeDate ?? null,
                noop, //onTargetUrlIconClick
                undefined,
                currentCampaign?.positionExcludedSerpFeatures,
            );
            const metricsDefinitions: IKeywordsReportTableMetrics[] = getTableMetrics(undefined, isSingleMode);
            const selectedMetricsDefinitions: IKeywordsReportTableMetrics[] = [];
            if (this._params.tableTab === EKeywordsReportKeywordsTableMetrics.ALL) {
                const positionsDefinitions = metricsDefinitions.find(
                    (metric) => metric.metricID === EKeywordsReportKeywordsTableMetrics.POSITION,
                );
                const clicksDefinitions = metricsDefinitions.find(
                    (metric) => metric.metricID === EKeywordsReportKeywordsTableMetrics.CLICKS,
                );
                selectedMetricsDefinitions.push(positionsDefinitions, clicksDefinitions);
            } else {
                selectedMetricsDefinitions.push(
                    metricsDefinitions.find((metric) => metric.metricID === this._params.tableTab),
                );
            }
            const allowedColumns = isSingleMode
                ? this.getSingleModeColumns(selectedMetricsDefinitions)
                : this.getCompareModeColumns(allSites, selectedMetricsDefinitions[0]); //in compare mode we have only one metric, "all" is not allowed
            const columns = rawColumns
                .filter((column) => allowedColumns.includes(column.key))
                .map((column) => {
                    return {
                        ...column,
                        title: this.getTableColumnsHeader(column),
                    };
                });
            return columns;
        } catch (error) {
            Sentry.error(
                "Error on dasbohrads during columns config creation for rank tracker keywords table at getMetricColumnsConfig - ",
                error,
            );
        }
    }
}

RankTrackerKeywordsReportTableWidget.register();
